import axios from "axios";
import {makeAutoObservable} from "mobx";
import {TOAST_TTL_MS} from "config";
import {translate} from "i18n/translator";
import type {LocalizedText} from "i18n/text";
import {reprNetworkError} from "utils";

export interface Toast {
    /** What failed — the action the user took. */
    title: LocalizedText;
    /** Why, in words they can act on. */
    body: LocalizedText;
}

/** Nothing came back that explains the failure: no answer at all, or a server error
 *  without the backend's own code (a crash, a proxy's 502). */
export const isTransportFailure = (error: unknown): boolean =>
    axios.isAxiosError(error)
    && (!error.response || (error.response.status >= 500 && !error.response.data?.error?.code));

/** Why a request failed, told the way its user can act on it: their connection, our
 *  server, or the refusal the backend explained. */
export const failureReason = (error: unknown): LocalizedText => {
    if (axios.isAxiosError(error)) {
        if (!error.response) return () => translate('error-toast-network');
        if (error.response.status >= 500) return () => translate('error-toast-server');
        if (error.response.data?.error?.code) {
            const {code, translationArgs} = reprNetworkError(error);
            // The backend's code is `<type>:<name>`; its copy is keyed by the name. A code
            // with no copy of ours is not shown as the server's message: that can carry
            // diagnostics (a Stripe request id) and is in English whatever the language.
            const key = `error_type__${code.split(':').pop()}`;
            return () => translate(key, translationArgs, translate('error-toast-unknown'));
        }
        // A throttle that answered in DRF's own shape, without our code.
        if (error.response.status === 429) return () => translate('error_type__rate_limit');
    }
    return () => translate('error-toast-unknown');
};

/** The error toast over the page (`AppToast`): one at a time, a newer one takes its place. */
export class ToastStore {
    toast: Toast | null = null;
    private timer: ReturnType<typeof setTimeout> | undefined;

    constructor() {
        makeAutoObservable(this);
    }

    /** A request that failed: `titleKey` (a locale key) names the action, the failure
     *  itself says why — or `body`, when the caller knows the failure better (a card decline). */
    error(titleKey: string, error: unknown, body: LocalizedText = failureReason(error)) {
        console.warn("request failed", error);
        clearTimeout(this.timer);
        this.toast = {title: () => translate(titleKey), body};
        this.timer = setTimeout(() => this.dismiss(), TOAST_TTL_MS);
    }

    dismiss() {
        clearTimeout(this.timer);
        this.toast = null;
    }
}
